import React, { useMemo } from 'react';
import { ToggleLeft, ToggleRight, FlaskConical, RotateCcw, XCircle } from 'lucide-react';
import GroupedRowActionSheet, { ICON_PROPS } from '../ui/GroupedRowActionSheet';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

/**
 * Per-user row actions for Admin → Users:
 *  - pending invite rows: resend / revoke the invite
 *  - accepted rows: enable / disable org access
 *  - (super admin) flag / unflag the account as a read-only demo user
 */
export default function AdminUsersRowActionsMenu({
  row,
  onToggleStatus,
  onToggleDemo,
  onResendInvite,
  onRevokeInvite,
  showDemoToggle = false,
  toggleAccessFeatureKey = 'action.admin.user.toggleAccess',
  toggleDemoFeatureKey = 'action.system.user.toggleDemo',
  inviteFeatureKey = 'action.admin.user.invite',
}) {
  const { lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const title = `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email || 'User';
  const isPending = row.status === 'pending';
  const isActive = row.active === 1;
  const isDemo = row.isDemoUser === 1;

  const items = useMemo(() => {
    if (isPending) {
      return [
        {
          key: 'resend',
          icon: <RotateCcw {...ICON_PROPS} />,
          label: 'Resend invite',
          ...lockProps(inviteFeatureKey),
        },
      ];
    }
    const list = [
      {
        key: 'toggle',
        icon: isActive ? <ToggleLeft {...ICON_PROPS} /> : <ToggleRight {...ICON_PROPS} />,
        label: isActive ? 'Disable access' : 'Enable access',
        ...lockProps(toggleAccessFeatureKey),
      },
    ];
    if (showDemoToggle) {
      list.push({
        key: 'demo',
        icon: <FlaskConical {...ICON_PROPS} />,
        label: isDemo ? 'Remove demo user' : 'Make demo user',
        ...lockProps(toggleDemoFeatureKey),
      });
    }
    return list;
  }, [isPending, isActive, isDemo, showDemoToggle, lockProps, toggleAccessFeatureKey, toggleDemoFeatureKey, inviteFeatureKey]);

  const dangerItem = isPending ? {
    key: 'revoke',
    icon: <XCircle {...ICON_PROPS} />,
    label: 'Revoke invite',
    ...lockProps(inviteFeatureKey),
  } : undefined;

  const handleAction = (key) => {
    if (key === 'toggle') {
      onToggleStatus(row);
    } else if (key === 'demo') {
      onToggleDemo?.(row);
    } else if (key === 'resend') {
      onResendInvite?.(row);
    } else if (key === 'revoke') {
      onRevokeInvite?.(row);
    }
  };

  return (
    <>
      <GroupedRowActionSheet
        entityLabel="User"
        title={title}
        menuAriaLabel="User actions"
        layoutKey={`${row.userId || row.studentId || row.inviteId}-${isActive}-${isDemo}-${isPending}`}
        items={items}
        dangerItem={dangerItem}
        onAction={handleAction}
      />
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}
